import { ChipMarker } from "@components/frame-editor/chip-markers";
import { useCallback, useMemo } from "react";
import { ChipEditData, useStudio } from "./use-studio";

/**
 * Hook for managing chip markers in the studio.
 * Provides handlers for adding, removing, moving, and selecting chips.
 */
export const useChipHandlers = () => {
  const { studio, setStudio } = useStudio();

  const chips = useMemo(() => studio?.chips || [], [studio?.chips]);
  const chipEdits = useMemo(() => studio?.chipEdits || [], [studio?.chipEdits]);
  const selectedChipId = studio?.selectedChipId;

  const handleAddChip = useCallback(
    (chip: ChipMarker) => {
      const newChips = [...chips, chip];
      const newChipEdit: ChipEditData = {
        chipId: chip.id,
        prompt: "",
        attachment: null,
        loraId: "none",
        mode: "none", // Start with attachment section collapsed
      };
      setStudio({
        chips: newChips,
        chipEdits: [...chipEdits, newChipEdit],
        selectedChipId: chip.id,
      });
    },
    [chips, chipEdits, setStudio]
  );

  const handleRemoveChip = useCallback(
    (chipId: string) => {
      const filteredChips = chips
        .filter((c) => c.id !== chipId)
        .map((chip, index) => ({ ...chip, number: index + 1 }));

      const filteredEdits = chipEdits.filter((e) => e.chipId !== chipId);

      const newSelectedChipId =
        selectedChipId === chipId
          ? filteredChips.length > 0
            ? filteredChips[0].id
            : null
          : selectedChipId;

      setStudio({
        chips: filteredChips,
        chipEdits: filteredEdits,
        selectedChipId: newSelectedChipId,
      });
    },
    [chips, chipEdits, selectedChipId, setStudio]
  );

  const handleMoveChip = useCallback(
    (chipId: string, x: number, y: number) => {
      setStudio({
        chips: chips.map((chip) =>
          chip.id === chipId ? { ...chip, x, y } : chip
        ),
      });
    },
    [chips, setStudio]
  );

  const handleSelectChip = useCallback(
    (chipId: string | null) => {
      setStudio({ selectedChipId: chipId });
    },
    [setStudio]
  );

  return {
    chips,
    chipEdits,
    selectedChipId,
    handleAddChip,
    handleRemoveChip,
    handleMoveChip,
    handleSelectChip,
  };
};
